import React, { useCallback, useEffect, useMemo, useState } from "react"
import { useHistory } from "react-router-dom"
import { Loader2, RefreshCcw } from "lucide-react"
import { toast } from "sonner"

import { InstrumentMarketDataRowEditor } from "@/components/Instruments/InstrumentMarketDataRowEditor"
import {
  BROKER_ALERT_TITLE_CLASS,
  BROKER_FIELD_DESC_CLASS,
} from "@/components/Broker/brokerAlertUi"
import { Button } from "@/components/ui/button"
import { Field, FieldDescription, FieldLabel } from "@/components/ui/field"
import { accountSettingsTabPath } from "@/constants/accountSettingsSidebar"
import { useActiveBrokerAccount } from "@/hooks/useActiveBrokerAccount"
import { cn } from "@/lib/utils"
import { scanInstrumentSetup } from "@/services/brokerIntegrationApi"
import {
  setMarketDataPreferencesBatch,
  skipMarketDataPreference,
} from "@/services/instrumentsApi"
import { formatTradeAccountPrimaryLabel } from "@/services/liquidBrokersApi"
import type {
  InstrumentSetupResolvedRow,
  InstrumentSetupResponse,
  InstrumentSetupRow,
} from "@/types/instrumentSetup"
import {
  formatInstrumentSetupListingSummary,
  instrumentSetupRowFromResolvedSummary,
} from "@/utils/instrumentSetupManageRow"
import {
  formatInstrumentSetupProgressDescription,
  instrumentSetupRowNeedsConfirmation,
} from "@/utils/instrumentMarketDataReadiness"
import {
  buildInstrumentSetupBatchItem,
  describeInstrumentSetupSaveBlockers,
  isInstrumentSetupRowReadyToSave,
  needsInstrumentSetupInitialConfirm,
  type InstrumentRowEditorSelection,
} from "@/utils/instrumentSetupSave"
import {
  disclaimerCopyForKey,
  inferDisclaimerKeyFromAttribution,
} from "@/constants/marketDataDisclaimers"
import {
  effectiveMarketDataAttributionForSetupRow,
  resolveMarketDataDisclaimer,
} from "@/utils/marketDataAttributionCopy"
import { refreshInstrumentSetupAfterMutation } from "@/utils/instrumentSetupLoad"
import { presentMutationError, toastError } from "@/utils/showApiErrorToast"
import { notifyTradesUpdated } from "@/hooks/useTradeStatistics"
import { AccountSettingsFieldSelect } from "./AccountSettingsFieldSelect"
import SettingCard, {
  accountSettingsCardStackClassName,
  accountSettingsFieldClassName,
  accountSettingsFieldRowClassName,
} from "./SettingCard"

type ListItem =
  | { kind: "pending"; row: InstrumentSetupRow }
  | { kind: "resolved"; row: InstrumentSetupResolvedRow }

function formatListingSummary(row: InstrumentSetupResolvedRow | InstrumentSetupRow): string {
  return formatInstrumentSetupListingSummary(row)
}

function formatSavedListingDisclaimerHint(
  row: InstrumentSetupResolvedRow | InstrumentSetupRow
): string | null {
  const attribution = effectiveMarketDataAttributionForSetupRow(row)
  if (attribution) {
    const sourceLabel =
      "market_data_source_label" in row ? row.market_data_source_label : undefined
    return resolveMarketDataDisclaimer(attribution, sourceLabel).collapsed
  }

  const broker = ("market_data_broker" in row && row.market_data_broker?.trim()) || ""
  if (!broker) return null

  const disclaimerKey = inferDisclaimerKeyFromAttribution({
    provider: broker,
    md_source: broker,
  })
  return disclaimerCopyForKey(disclaimerKey).collapsed
}

const InstrumentMarketDataSettings: React.FC = () => {
  const history = useHistory()
  const {
    accounts,
    activeAccountCode,
    loading: accountsLoading,
    refetch: refetchTradeAccounts,
  } = useActiveBrokerAccount()

  const accountOptions = useMemo(
    () =>
      accounts.map((account) => ({
        value: account.accountCode,
        label: formatTradeAccountPrimaryLabel(account),
      })),
    [accounts]
  )

  const [selectedAccountCode, setSelectedAccountCode] = useState<string | null>(
    activeAccountCode ?? null
  )
  const [loading, setLoading] = useState(true)
  const [scanning, setScanning] = useState(false)
  const [saving, setSaving] = useState(false)
  const [setupData, setSetupData] = useState<InstrumentSetupResponse | null>(null)
  const [selectedSymbol, setSelectedSymbol] = useState<string | null>(null)

  useEffect(() => {
    if (!selectedAccountCode && activeAccountCode) {
      setSelectedAccountCode(activeAccountCode)
    }
  }, [activeAccountCode, selectedAccountCode])

  const load = useCallback(async (accountCode: string, options?: { quiet?: boolean }) => {
    const quiet = options?.quiet === true
    if (!quiet) setLoading(true)
    try {
      // Settings page open / account switch: always POST …/scan.
      const data = quiet
        ? await refreshInstrumentSetupAfterMutation(accountCode)
        : await scanInstrumentSetup(accountCode)
      setSetupData(data)
    } catch (error) {
      presentMutationError(error, "Failed to load instrument market data")
      if (!quiet) setSetupData(null)
    } finally {
      if (!quiet) setLoading(false)
    }
  }, [])

  useEffect(() => {
    if (selectedAccountCode) return
    setSetupData(null)
    setSelectedSymbol(null)
    if (!accountsLoading && accounts.length === 0) {
      setLoading(false)
    }
  }, [selectedAccountCode, accountsLoading, accounts.length])

  useEffect(() => {
    if (!selectedAccountCode) return
    void load(selectedAccountCode)
  }, [selectedAccountCode, load])

  const listItems = useMemo<ListItem[]>(() => {
    if (!setupData) return []
    const pending = (setupData.pending ?? []).map((row) => ({
      kind: "pending" as const,
      row,
    }))
    const resolved = (setupData.resolved ?? []).map((row) => ({
      kind: "resolved" as const,
      row,
    }))
    return [...pending, ...resolved]
  }, [setupData])

  useEffect(() => {
    if (listItems.length === 0) {
      setSelectedSymbol(null)
      return
    }
    if (
      selectedSymbol &&
      listItems.some((item) => item.row.symbol === selectedSymbol)
    ) {
      return
    }
    setSelectedSymbol(listItems[0].row.symbol)
  }, [listItems, selectedSymbol])

  const activeItem = useMemo(
    () => listItems.find((item) => item.row.symbol === selectedSymbol) ?? null,
    [listItems, selectedSymbol]
  )

  const symbolSelectOptions = useMemo(
    () =>
      listItems.map((item) => {
        const symbol = item.row.symbol
        if (instrumentSetupRowNeedsConfirmation(item.row)) {
          return {
            value: symbol,
            label: `${symbol} · Needs confirmation`,
          }
        }
        return {
          value: symbol,
          label: `${symbol} · ${formatListingSummary(item.row)}`,
        }
      }),
    [listItems]
  )

  // Editor is driven by GET …/instrument-setup only. Resolve runs inside the
  // row editor on debounced asset-class / quote edits (and on Confirm) — never
  // once-per-symbol on open or hover.

  const editorRow = useMemo(() => {
    if (!activeItem) return null
    if (activeItem.kind === "pending") return activeItem.row
    return instrumentSetupRowFromResolvedSummary(activeItem.row)
  }, [activeItem])

  const savedListingDisclaimerHint = useMemo(() => {
    if (!activeItem || activeItem.kind !== "resolved") return null
    return formatSavedListingDisclaimerHint(activeItem.row)
  }, [activeItem])

  const handleRefreshScan = useCallback(async () => {
    if (!selectedAccountCode) return
    setScanning(true)
    try {
      const data = await scanInstrumentSetup(selectedAccountCode)
      setSetupData(data)
      await refetchTradeAccounts()
      toast.success("Market data sources refreshed")
    } catch (error) {
      presentMutationError(error, "Failed to refresh instrument scan")
    } finally {
      setScanning(false)
    }
  }, [refetchTradeAccounts, selectedAccountCode])

  const handleSetupRefresh = useCallback(async () => {
    const code = selectedAccountCode?.trim()
    if (!code) return
    await load(code, { quiet: true })
    await refetchTradeAccounts()
  }, [load, refetchTradeAccounts, selectedAccountCode])

  const handleSaveRow = useCallback(
    async (
      row: InstrumentSetupRow,
      selection: InstrumentRowEditorSelection,
      consentGranted: boolean,
      resolveContext?: {
        md_listing_key?: string
        instrument_id?: string
        asset_class?: string
      }
    ): Promise<boolean> => {
      const code = selectedAccountCode?.trim()
      if (!code) return false
      if (!isInstrumentSetupRowReadyToSave({ row, selection, consentGranted })) {
        const blocker = describeInstrumentSetupSaveBlockers({
          row,
          selection,
          consentGranted,
        })
        toastError(blocker || "Complete all required fields before saving.")
        return false
      }

      setSaving(true)
      try {
        const batchItem = buildInstrumentSetupBatchItem(
          code,
          row.symbol.trim(),
          selection,
          resolveContext,
          row,
          consentGranted
        )
        const batchResponse = await setMarketDataPreferencesBatch({
          broker_account_code: code,
          preferences: [batchItem],
        })
        const saved = batchResponse.saved?.find((item) => item.symbol === row.symbol.trim())
        if (!batchResponse.ok || batchResponse.saved_count < 1 || !saved?.ok) {
          throw new Error(
            saved && !saved.ok
              ? `Market data preference for ${row.symbol} was not saved.`
              : "Market data preference save failed."
          )
        }

        const data = await refreshInstrumentSetupAfterMutation(code)
        setSetupData(data)
        await refetchTradeAccounts()
        notifyTradesUpdated({ action: "marketDataPreference", accountCode: code })
        toast.success(`Saved market data for ${row.symbol}`)
        return true
      } catch (error) {
        presentMutationError(error, `Failed to save ${row.symbol}`)
        return false
      } finally {
        setSaving(false)
      }
    },
    [refetchTradeAccounts, selectedAccountCode]
  )

  const handleSkipRow = useCallback(
    async (row: InstrumentSetupRow, reason: "unsupported" | "user_declined") => {
      const code = selectedAccountCode?.trim()
      if (!code) return
      setSaving(true)
      try {
        await skipMarketDataPreference({
          symbol: row.symbol.trim(),
          broker_account_code: code,
          reason,
        })
        const data = await refreshInstrumentSetupAfterMutation(code)
        setSetupData(data)
        await refetchTradeAccounts()
        toast.success(`Skipped ${row.symbol} — continuing without charts`)
      } catch (error) {
        presentMutationError(error, `Failed to skip ${row.symbol}`)
      } finally {
        setSaving(false)
      }
    },
    [refetchTradeAccounts, selectedAccountCode]
  )

  const isPageLoading = accountsLoading
  const setupLoading = Boolean(
    selectedAccountCode &&
      (loading || (setupData != null && setupData.broker_account_code !== selectedAccountCode))
  )

  if (isPageLoading) {
    return (
      <div className={accountSettingsCardStackClassName} data-account-settings-card-stack="">
        <SettingCard
          title="Instrument market data"
          description="Loading trade accounts…"
        >
          <div className={cn(BROKER_FIELD_DESC_CLASS, "flex items-center gap-2")}>
            <Loader2 className="size-4 animate-spin" aria-hidden />
            Loading…
          </div>
        </SettingCard>
      </div>
    )
  }

  if (accounts.length === 0) {
    return (
      <div className={accountSettingsCardStackClassName} data-account-settings-card-stack="">
        <SettingCard
          title="Instrument market data"
          description="Connect a broker account first, then configure OHLC market data sources for your traded symbols."
        >
          <Button
            type="button"
            variant="outline-brand"
            size="sm"
            data-font="primary"
            className="h-8 px-5 text-[0.75rem] normal-case tracking-normal"
            onClick={() => history.push(accountSettingsTabPath("broker-api"))}
          >
            Open Broker Management
          </Button>
        </SettingCard>
      </div>
    )
  }

  return (
    <div className={accountSettingsCardStackClassName} data-account-settings-card-stack="">
      <SettingCard
        title="Instrument market data"
        description="Review and change the OHLC market data source Tessera uses for each traded symbol on an account. Re-saving overwrites the previous preference."
      >
        <Field className={cn(accountSettingsFieldClassName, "w-full max-w-md")}>
          <FieldLabel className={BROKER_ALERT_TITLE_CLASS}>Trade account</FieldLabel>
          <FieldDescription className={BROKER_FIELD_DESC_CLASS}>
            Market data preferences are stored per trade account.
          </FieldDescription>
          <div className={accountSettingsFieldRowClassName}>
            <div className="min-w-0 flex-1">
              <AccountSettingsFieldSelect
                id="instrument-market-data-account"
                value={selectedAccountCode ?? ""}
                onValueChange={(value) => setSelectedAccountCode(value || null)}
                placeholder="Select account"
                options={
                  accountOptions.length > 0
                    ? accountOptions
                    : [{ value: "__none", label: "No accounts", disabled: true }]
                }
                disabled={accountOptions.length === 0}
              />
            </div>
            <Button
              type="button"
              variant="outline-gray"
              size="sm"
              data-font="primary"
              data-reports-rename-button=""
              className="h-8 shrink-0 px-5 text-[0.75rem] normal-case tracking-normal"
              disabled={!selectedAccountCode || scanning || loading}
              onClick={() => void handleRefreshScan()}
            >
              {scanning ? (
                <Loader2 className="size-4 animate-spin" aria-hidden />
              ) : (
                <RefreshCcw className="size-4" aria-hidden />
              )}
              Refresh scan
            </Button>
          </div>
        </Field>
      </SettingCard>

      <SettingCard
        title="Symbols"
        description={formatInstrumentSetupProgressDescription(
          setupData,
          "Select an account to load configured instruments."
        )}
      >
        {setupLoading ? (
          <div className={cn(BROKER_FIELD_DESC_CLASS, "flex min-h-[8rem] items-center gap-2")}>
            <Loader2 className="size-4 animate-spin" aria-hidden />
            Loading instrument setup…
          </div>
        ) : listItems.length === 0 ? (
          <p className={cn(BROKER_FIELD_DESC_CLASS, "m-0")}>
            No traded symbols found for this account yet. Sync or import trades, then refresh the
            scan.
          </p>
        ) : (
          <div className="flex w-full min-w-0 flex-col gap-4">
            <Field className={cn(accountSettingsFieldClassName, "w-full max-w-md")}>
              <FieldLabel className={BROKER_ALERT_TITLE_CLASS}>Symbol</FieldLabel>
              <FieldDescription className={BROKER_FIELD_DESC_CLASS}>
                Pick a traded symbol to review or change its OHLC market data source.
              </FieldDescription>
              <AccountSettingsFieldSelect
                id="instrument-market-data-symbol"
                value={selectedSymbol ?? ""}
                onValueChange={(value) => setSelectedSymbol(value || null)}
                placeholder="Select symbol"
                options={symbolSelectOptions}
              />
            </Field>

            {savedListingDisclaimerHint ? (
              <p className={cn(BROKER_FIELD_DESC_CLASS, "m-0 leading-5")}>
                {savedListingDisclaimerHint}
              </p>
            ) : null}

            {editorRow && selectedAccountCode ? (
              <InstrumentMarketDataRowEditor
                key={`${selectedAccountCode}:${selectedSymbol ?? ""}`}
                row={editorRow}
                brokerAccountCode={selectedAccountCode}
                assetClassHints={setupData?.broker_asset_class_hints}
                alwaysShowSourcePicker
                requireDirtyToSave={
                  editorRow != null && !needsInstrumentSetupInitialConfirm(editorRow)
                }
                saving={saving}
                compactHeader
                onSetupRefresh={handleSetupRefresh}
                onSkip={(reason) => handleSkipRow(editorRow, reason)}
                onSave={(selection, consentGranted, resolveContext) =>
                  handleSaveRow(editorRow, selection, consentGranted, resolveContext)
                }
              />
            ) : (
              <p className={cn(BROKER_FIELD_DESC_CLASS, "m-0")}>
                Select a symbol to view or change its market data source.
              </p>
            )}
          </div>
        )}
      </SettingCard>
    </div>
  )
}

export default InstrumentMarketDataSettings
